import { rem } from 'polished';
import React from 'react';
import styled from 'styled-components';

import {
  ForbiddenError,
  RateLimitError,
  ResourceNotFoundError,
  UnauthorizedError,
} from '@vimeo/core/libs/errors';
import { translate } from '@vimeo/i18n-runtime';
import { type ErrorPageDisplayedProps, useErrorPageTracking, usePicoEffect } from '@vimeo/picox';

import { ClipsFetchError } from '../../errors';

const Container = styled.div`
  margin-top: ${rem(40)};
  text-align: center;
`;

const Title = styled.h1`
  margin-top: 0;
  margin-bottom: ${rem(10)};
`;

const Content = styled.div`
  margin: 0 auto;
  max-width: ${rem(660)};
  padding: 0 ${rem(15)} ${rem(10)};
`;

const Message = styled.div`
  margin-bottom: ${rem(30)};
`;

export const View: React.FC<{ title: string; message: string }> = ({ title, message }) => (
  <Container>
    <Title>{title}</Title>
    <Content>
      <Message>{message}</Message>
    </Content>
  </Container>
);

export interface Props {
  error: Error;
}

export default function ErrorPage({ error }: Props) {
  const { trackErrorPageDisplayed } = useErrorPageTracking();
  const errorMapping: Pick<ErrorPageDisplayedProps, 'errorType' | 'errorPageErrorCode'> =
    error instanceof RateLimitError
      ? { errorType: 'rate_limit', errorPageErrorCode: 429 }
      : error instanceof ResourceNotFoundError
        ? { errorType: 'resource_not_found', errorPageErrorCode: 404 }
        : error instanceof ForbiddenError
          ? { errorType: 'forbidden', errorPageErrorCode: 403 }
          : error instanceof UnauthorizedError
            ? { errorType: 'unauthorized', errorPageErrorCode: 401 }
            : error instanceof ClipsFetchError
              ? { errorType: 'clips_fetch', errorPageErrorCode: null }
              : { errorType: 'technical_difficulties', errorPageErrorCode: null };

  usePicoEffect(
    () => {
      trackErrorPageDisplayed({ errorPageType: 'showcase', ...errorMapping });
    },
    [],
    { once: true },
  );

  switch (error.constructor) {
    default:
      window.DD_RUM &&
        window.DD_RUM.onReady(() => {
          window.DD_RUM.addError(error, { component: 'TechnicalDifficulties' });
        });
      return (
        <View
          title={translate('Technical Difficulties')}
          message={translate(
            'Whoops! We are having some technical difficulties, please try again in a minute.',
          )}
        />
      );

    case RateLimitError:
      return (
        <View
          title={translate('Whoa there!')}
          message={translate(
            'Sorry, you have made too many requests in a short period of time. Please wait a few minutes and try again.',
          )}
        />
      );

    case ResourceNotFoundError:
      return (
        <View
          title={translate('Sorry, we couldn’t find that page')}
          message={translate('Make sure you’ve typed the URL correctly, or try searching Vimeo.')}
        />
      );

    case ForbiddenError:
      return (
        <View
          title={translate('Permission Denied')}
          message={translate(
            'Sorry but you do not have the proper permission to access this area of Vimeo.',
          )}
        />
      );

    case UnauthorizedError:
      return (
        <View
          title={translate('Unauthorized')}
          message={translate('You are unauthorized for this action.')}
        />
      );

    case ClipsFetchError:
      return (
        <View
          title={translate('Sorry, there was an error')}
          message={translate('There was a problem loading these videos. Refresh to try again.')}
        />
      );
  }
}
